import {
	useSupergridMutations, getLevelByDepth, getLevelDepth, useSuperGridValidate,
} from '@aleph/supergrid';
import {
	FC, useEffect, useState,
} from 'react';
import classNames from 'classnames';
import {
	Button,
	ButtonAppearance,
	ButtonColor,
	ButtonSize,
	MiddleEllipsisWhisper,
	OpenIcon,
	PlusIcon,
	TrashCanIcon,
	useUrlParams,
} from '@aleph/shared-components';
import { startCase } from 'lodash';
import { ICellRendererParams } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { SpotifyBreakdowns, SpotifyLevel, SpotifyLevelId } from 'wb-spotify/app/routes/superGrid/utils';
import {
	generateMutationHash,
	SPOTIFY_DEFAULT_CREATE_VALUES,
	SpotifyCreateLevels,
	SpotifySuperGridMutationsConfig,
} from 'wb-spotify/app/routes/superGrid/mutator/utils';
import { SPOTIFY_SUPERGRID_CONFIG } from 'wb-spotify/app/routes/superGrid/config';
import { useNavigate, useParams } from 'react-router-dom';
import { processParent } from 'wb-spotify/app/routes/superGrid/helpers';

const NameCellRenderer: FC<ICellRendererParams> = (params) => {
	const { t } = useTranslation();
	const [anyAdAccountRowExpanded, setAnyAdAccountRowExpanded] = useState(false);
	const navigate = useNavigate();

	const { setUrlParams, params: urlParams } = useUrlParams({ navigate, useParams });
	const isDashboardView = params.data?.isDashboardView && !params.node.expanded;
	const { context: { setValidations } } = useSuperGridValidate();

	const {
		removeMutationTree,
		addMutation,
		mutations,
	} = useSupergridMutations<SpotifySuperGridMutationsConfig>();
	const { breakdown } = params?.context ?? {};

	const removeMutationTreeAndValidations = (hash: string) => {
		removeMutationTree(hash);
		setValidations((curr) => curr.filter((validation) => validation.id !== hash));
	};

	const omitViewToEntity = () => {
		let nextLevel: SpotifyLevel = SpotifyLevel.CAMPAIGN;

		if (params.data?.level === SpotifyLevel.CAMPAIGN) {
			nextLevel = SpotifyLevel.GROUP;
		}

		if (params.data?.level === SpotifyLevel.GROUP) {
			nextLevel = SpotifyLevel.AD;
		}

		setUrlParams({
			...urlParams,
			filters: {
				[SpotifyLevelId[params.data?.level]]: [{ value: [params.data?.id] }],
			},
			level: nextLevel,
		});
	};

	const addEntity = () => {
		let currLevelDepth = getLevelDepth(SPOTIFY_SUPERGRID_CONFIG, params.data?.level);
		let creatingLevel = getLevelByDepth(SPOTIFY_SUPERGRID_CONFIG, currLevelDepth);
		let currParent = processParent(params.node?.parent?.data ?? params?.data?.parent);

		while (creatingLevel !== SpotifyLevel.BREAKDOWN) {
			const newMutation = {
				...(SPOTIFY_DEFAULT_CREATE_VALUES[creatingLevel]?.(currParent) ?? {}),
				name:   `New ${startCase(creatingLevel)} ${(mutations?.[creatingLevel]?.length ?? 0) + 1}`,
				parent: currParent,
				hash:   generateMutationHash(),
			};

			addMutation(creatingLevel as unknown as SpotifyCreateLevels, newMutation);

			currParent = newMutation;
			creatingLevel = getLevelByDepth(SPOTIFY_SUPERGRID_CONFIG, ++currLevelDepth);
		}
	};

	const pinnedWidth = params.columnApi?.getDisplayedLeftColumns()?.reduce(
		(accu, col) => accu + (col?.getActualWidth() ?? 0),
		0,
	);

	useEffect(
		() => {
			if (params.node?.data?.superGridLevel === SpotifyLevel.AD_ACCOUNT) {
				let expanded = false;

				params.api.forEachNode(node => {
					if (node.expanded) {
						expanded = true;
					}
				});

				setAnyAdAccountRowExpanded(expanded);
			}
		},
		[params],
	);

	return (
		<div
			className={classNames('w-full px-[8px] h-full max-h-full font-medium', {
				'text-[18px] ': isDashboardView && !anyAdAccountRowExpanded,
			})}
			data-testid="accounts"
		>
			<div
				className={classNames(
					'group flex justify-between gap-[8px] h-full items-center overflow-hidden',
					{
						'text-gray-800': breakdown
							&& breakdown !== SpotifyBreakdowns.ENTITIES
							&& params.data?.level !== SpotifyLevel.BREAKDOWN,
					},
				)}
			>
				{
					!params.data?.isCreate
					&& <div
						className="overflow-hidden w-full"
						key={pinnedWidth ?? 'name'}
					>
						<MiddleEllipsisWhisper value={params.value} />
					</div>
				}

				{
					params.data?.isCreate && (
						<Button
							onClick={addEntity}
							leftNode={<PlusIcon fill="black" />}
							appearance={ButtonAppearance.GHOST}
							size={ButtonSize.SMALL}
							color={ButtonColor.GRAY}
							inGrid
							className="hover:!ring-transparent"
						/>
					)
				}

				<div className="group-hover:opacity-100 opacity-0 right-[8px] flex
						gap-[8px] items-center absolute p-[2px] bg-white rounded"
				>
					{
						!params.data?.isCreate
						&& !params.data?.isInCreation
						&& !(params.data?.level === SpotifyLevel.BREAKDOWN)
						&& params.data?.level !== SpotifyLevel.AD
						&& !params.data.supergridIsNoDataRow
						&& <Button
							data-testid="open-button"
							title={t('sc: open', 'Open')}
							rightNode={
								<OpenIcon />
							}
							onClick={omitViewToEntity}
							size={ButtonSize.SMALL}
							appearance={ButtonAppearance.OUTLINED}
							inGrid
						/>
					}

					{
						params.data?.level === SpotifyLevel.AD
							&& !params.data.supergridIsNoDataRow
							&& params.data.adPreviewUrl
							&& (
								<Button
									title={t('sc: preview', 'Preview')}
									size={ButtonSize.SMALL}
									appearance={ButtonAppearance.OUTLINED}
									inGrid
									as="a"
									href={params.data.adPreviewUrl}
									target="_blank"
								/>
							)
					}

					{!params.data?.id // lack of id proves that this entity is in creation and doesn't exist
						&& !params.data?.isCreate // don't show on + rows
						&& <Button
							leftNode={
								<TrashCanIcon />
							}
							onClick={() => removeMutationTreeAndValidations(params.data?.hash)}
							size={ButtonSize.SMALL}
							appearance={ButtonAppearance.OUTLINED}
							color={ButtonColor.RED}
							data-testid="remove-entry-button"
							inGrid
						/>
					}
				</div>
			</div>
		</div>
	);
};

export default NameCellRenderer;
